/* XNote Write PDP, Studio Light (docs/design-v3-light.md §4, §7.6; PDP r4 docs/build/pdp-r4/PLAN.md §5–§6, build notes
   docs/build/pdp-r4/build-sections.md). Loaded deferred by sections/xp-write-buy.liquid; no dependencies. Every part
   works without it (the storyboard shows three stills, the outcomes stack, the photos stay in the page). The S1 buy
   stage (gallery, zoom, 360, 3D, quantity cards, add-ons, buy mirrors) lives in xp-write-buy.js (PLAN §8 B0).
   - S3 storyboard (initDemo): Write → Sync → Convert play in sequence while the board is ≥ 50 % in view (after load,
     tab visible, not reduced motion or Save-Data), two rounds, then it rests on Convert. The rail is an ARIA tablist
     (roving tabindex, arrows, Home/End); a tap, click, key or swipe stops the sequence and plays the chosen beat once
     (never under reduced motion: posters only). Offscreen or in a hidden tab it pauses. A focus-only pause toggle
     covers WCAG 2.2.2.
   - S3 film dialog (initFilmDialog + initFilm): "Watch the demo" opens the chaptered film in a modal <dialog>; the
     sources are set on open and removed on close, focus returns to the button. The player: no native controls, one
     play/pause button, the chapter rail seeks and marks the current chapter; the desktop cut plays muted once the dialog
     shows it (not under reduced motion or Save-Data), starting on the Write chapter; a viewer's pause sticks.
   - S4 "Day to day" (initUses): four tabs; each outcome's visual builds when it opens (.xp-feat.is-on). While the
     stage is ≥ 50 % in view the tabs advance every 6.5 s, two rounds, then it rests on Find; hover or focus inside
     pauses it, a click, key or swipe stops it.
   - S7 reviews hero (initReviewHero): the photo tiles open a light <dialog> (previous / next, arrow keys, Esc); a photo
     that fails to load hides the photo row (the hero shows as text).
   - Polish round 1: the storyboard opens on Sync on phones and, under reduced motion or Save-Data, rests on Sync
     (phones) or Convert (desktop); S5 costs gets a three-dot pager on phones (initCosts). */
(() => {
  'use strict';
  const d = document;
  const w = window;
  const root = d.documentElement;
  const $ = (s, r = d) => r.querySelector(s);
  const $$ = (s, r = d) => Array.from(r.querySelectorAll(s));
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const phone = matchMedia('(max-width: 767px)');
  const conn = navigator.connection || {};
  const lite = () => root.classList.contains('xp-lite') || Boolean(conn.saveData) || /2g$/.test(conn.effectiveType || '');
  const still = () => reduced.matches || lite();
  const hasIO = 'IntersectionObserver' in w;
  const setPoster = (v) => { if (v && v.dataset.xpPoster) { v.poster = v.dataset.xpPoster; delete v.dataset.xpPoster; } };
  const playSafe = (v) => { const p = v.play(); if (p && p.catch) p.catch(() => {}); };
  const afterLoad = (fn) => (d.readyState === 'complete' ? fn() : w.addEventListener('load', fn, { once: true }));
  const PAUSE_SVG = '<svg class="xp-media__pause-i" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M8 5.5h3v13H8zM13 5.5h3v13h-3z" fill="currentColor"/></svg><svg class="xp-media__play-i" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M8 5.5v13l10.5-6.5z" fill="currentColor"/></svg>';

  /* An ARIA tablist over existing buttons and panels: roles, roving tabindex, arrow keys / Home / End.
     onPick(i, how) runs for a click or a key; the returned select(i) only updates the state. */
  function tablist(rail, tabs, panels, onPick) {
    rail.setAttribute('role', 'tablist');
    rail.hidden = false;
    tabs.forEach((t, i) => {
      t.setAttribute('role', 'tab');
      t.setAttribute('aria-controls', panels[i].id);
      panels[i].setAttribute('role', 'tabpanel');
      panels[i].setAttribute('aria-labelledby', t.id);
    });
    const select = (i) => {
      tabs.forEach((t, j) => { t.setAttribute('aria-selected', String(i === j)); t.tabIndex = i === j ? 0 : -1; });
      panels.forEach((p, j) => p.classList.toggle('is-on', i === j));
    };
    rail.addEventListener('keydown', (e) => {
      const i = tabs.indexOf(d.activeElement);
      if (i < 0) return;
      const n = { ArrowRight: i + 1, ArrowDown: i + 1, ArrowLeft: i - 1, ArrowUp: i - 1, Home: 0, End: tabs.length - 1 }[e.key];
      if (n === undefined) return;
      e.preventDefault();
      const k = (n + tabs.length) % tabs.length;
      onPick(k, 'key');
      tabs[k].focus();
    });
    tabs.forEach((t, i) => t.addEventListener('click', () => onPick(i, 'click')));
    return select;
  }

  /* A horizontal swipe on el calls fn(+1 | -1); vertical scrolling is left alone (touch-action: pan-y in the CSS). */
  function onSwipe(el, fn) {
    let x0 = null, y0 = 0, id = null;
    el.addEventListener('pointerdown', (e) => { if (e.pointerType === 'mouse') return; x0 = e.clientX; y0 = e.clientY; id = e.pointerId; });
    el.addEventListener('pointerup', (e) => {
      if (x0 === null || e.pointerId !== id) return;
      const dx = e.clientX - x0, dy = e.clientY - y0;
      x0 = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.3) { el._xpSwiped = Date.now(); fn(dx < 0 ? 1 : -1); }
    });
    el.addEventListener('pointercancel', () => { x0 = null; });
  }

  /* A focus-only pause toggle (WCAG 2.2.2), as on the other looping media: invisible until focused. */
  function pauseToggle(host, label, onToggle) {
    const btn = d.createElement('button');
    btn.type = 'button';
    btn.className = 'xp-media__pause';
    btn.setAttribute('aria-label', `Pause the ${label}`);
    btn.setAttribute('aria-pressed', 'false');
    btn.innerHTML = PAUSE_SVG;
    btn.hidden = still();
    host.appendChild(btn);
    let paused = false;
    btn.addEventListener('click', () => {
      paused = !paused;
      btn.setAttribute('aria-pressed', String(paused));
      btn.setAttribute('aria-label', `${paused ? 'Play' : 'Pause'} the ${label}`);
      onToggle(paused);
    });
    reduced.addEventListener('change', () => { btn.hidden = still(); });
    return btn;
  }

  /* ---------- S3 storyboard ---------- */
  function initDemo() {
    const sec = $('[data-xp-write-demo]');
    if (!sec) return;
    const board = $('.xp-write-demo__board', sec);
    const rail = $('[data-xp-demo-rail]', sec);
    const tabs = $$('[data-xp-demo-tab]', sec);
    const beats = $$('[data-xp-demo-beat]', sec);
    if (!board || !rail || !beats.length || tabs.length !== beats.length) return;
    const vids = beats.map((b) => $('video', b));
    const bars = tabs.map((t) => $('.xp-write-demo__bar i', t));
    const holds = beats.map((b) => Number(b.dataset.hold) || 8000);
    sec.classList.add('is-js');
    // Polish round 1: phones show one frame, so they open on Sync (the page appearing in the app), not on a hand writing;
    // under reduced motion or Save-Data nothing plays, so the board rests on a payoff: Sync on phones, Convert on desktop.
    const first = phone.matches ? 1 : 0;
    const rest = phone.matches ? 1 : beats.length - 1;
    let cur = still() ? rest : first, round = 0, auto = true, userBeat = false, paused = false, inView = false, loaded = d.readyState === 'complete';
    let timer = 0, raf = 0, startAt = 0, spent = 0, running = false, waitT = 0;
    const setBar = (i, p) => { if (bars[i]) bars[i].style.setProperty('--p', Math.max(0, Math.min(1, p)).toFixed(3)); };
    const clearTimers = () => { clearTimeout(timer); clearTimeout(waitT); cancelAnimationFrame(raf); timer = 0; waitT = 0; raf = 0; };
    const tick = () => { setBar(cur, (spent + performance.now() - startAt) / holds[cur]); raf = requestAnimationFrame(tick); };
    const playing = () => sec.classList.toggle('is-playing', running);
    // Stop the running beat where it is (its frame keeps its current picture), keep the time already spent.
    const halt = () => {
      if (running) spent += performance.now() - startAt;
      running = false;
      clearTimers();
      const v = vids[cur];
      if (v && !v.paused) v.pause();
      playing();
    };
    const canAuto = () => auto && !paused && inView && loaded && !d.hidden && !still();
    const canRun = () => (auto ? canAuto() : userBeat && inView && !d.hidden && !paused && !reduced.matches);
    const finish = () => {
      running = false;
      clearTimers();
      setBar(cur, 1);
      const v = vids[cur];
      if (v && v.loop) v.pause();
      if (!auto) { userBeat = false; playing(); return; }
      if (cur === beats.length - 1) {
        round += 1;
        if (round >= 2) { auto = false; playing(); return; } // rests on Convert, its text end state
      }
      go((cur + 1) % beats.length, false);
      run();
    };
    // Start (or resume) the current beat: its clip plays from its start, the bar fills over the beat's hold.
    const run = () => {
      if (running || !canRun()) { if (!canRun()) halt(); return; }
      const v = vids[cur];
      running = true;
      playing();
      const begin = () => {
        if (!running) return;
        startAt = performance.now();
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(tick);
        clearTimeout(timer);
        timer = setTimeout(finish, Math.max(0, holds[cur] - spent));
      };
      if (!v) { begin(); return; }
      setPoster(v);
      if (v.preload === 'none') v.preload = 'auto';
      if (spent === 0 && v.readyState >= 1) { try { v.currentTime = 0; } catch (_) { /* not seekable yet */ } }
      if (v.networkState === 3) v.load();
      const onPlay = () => { clearTimeout(waitT); begin(); };
      v.addEventListener('playing', onPlay, { once: true });
      waitT = setTimeout(() => { v.removeEventListener('playing', onPlay); begin(); }, 3500); // slow network: move on
      playSafe(v);
    };
    // Show beat i (phones: the one frame; desktop: the active frame). Bars before it are full in the sequence.
    const go = (i, user) => {
      halt();
      const prev = vids[cur];
      if (prev && i !== cur) { prev.pause(); }
      cur = i;
      spent = 0;
      select(i);
      bars.forEach((_, j) => setBar(j, user ? (j === i ? 0 : 0) : (j < i ? 1 : 0)));
    };
    const pick = (i) => {
      auto = false;
      userBeat = !reduced.matches;
      go(i, true);
      run();
    };
    const select = tablist(rail, tabs, beats, (i) => pick(i));
    select(cur);
    beats.forEach((b, i) => b.addEventListener('click', () => { if (Date.now() - (board._xpSwiped || 0) < 400) return; pick(i); }));
    onSwipe($('[data-xp-demo-beats]', sec), (dir) => { if (phone.matches) pick((cur + dir + beats.length) % beats.length); });
    pauseToggle(board, 'demo', (p) => { paused = p; if (p) halt(); else run(); });
    if (!hasIO) return;
    new IntersectionObserver((es) => es.forEach((e) => { inView = e.intersectionRatio >= 0.5; if (inView) run(); else halt(); }), { threshold: [0, 0.5] }).observe(board);
    d.addEventListener('visibilitychange', () => { if (d.hidden) halt(); else run(); });
    reduced.addEventListener('change', () => { if (reduced.matches) { auto = false; userBeat = false; halt(); go(rest, false); } });
    afterLoad(() => { loaded = true; run(); });
  }

  /* ---------- S3 film (in its dialog) ---------- */
  function initFilm() {
    const sec = $('[data-xp-write-film]');
    if (!sec) return;
    const films = $$('video.xp-write-film__v', sec);
    const screen = $('.xp-write-film__screen', sec);
    const btn = $('[data-xp-write-film-play]', sec);
    const rail = $('.xp-write-film__rail', sec);
    const chs = $$('.xp-write-film__ch', sec);
    films.forEach((v) => { v.controls = false; v.removeAttribute('controls'); });
    if (btn) btn.hidden = false;
    if (rail) rail.hidden = false;
    sec.classList.add('is-js');
    const visible = () => films.find((v) => v.getClientRects().length > 0);
    const key = (v) => (v && v.classList.contains('xp-write-film__v--m') ? 'm' : 'd');
    const span = (c, k) => {
      const a = c.dataset[`t${k.toUpperCase()}`];
      const b = c.dataset[`t${k.toUpperCase()}End`];
      return a === '' || a === undefined ? null : [Number(a), Number(b)];
    };
    // Layout C of the desktop cut (Convert, Summarize, Share) is one phone on the night card: the chapter's own rail
    // label shows beside it with its progress underneath (aria-hidden: the rail carries the same words).
    const word = screen && d.createElement('div');
    if (word) {
      word.className = 'xp-write-film__word';
      word.setAttribute('aria-hidden', 'true');
      chs.forEach((c) => { word.appendChild(d.createElement('span')).textContent = c.textContent.trim(); });
      word.appendChild(d.createElement('i')).appendChild(d.createElement('b'));
      screen.appendChild(word);
    }
    const mark = (v) => {
      const k = key(v);
      const t = v.currentTime;
      chs.forEach((c, n) => {
        const s = span(c, k);
        const on = !!s && t >= s[0] && t < s[1] && (t > 0 || !v.paused);
        c.classList.toggle('is-on', on);
        if (on) c.setAttribute('aria-current', 'true'); else c.removeAttribute('aria-current');
        const bar = $('.xp-write-film__bar i', c);
        const f = s ? Math.min(1, Math.max(0, (t - s[0]) / (s[1] - s[0]))) : 0;
        if (bar) bar.style.transform = `scaleX(${(t >= (s ? s[1] : Infinity) ? 1 : f).toFixed(3)})`;
        if (word && k === 'd') {
          word.children[n].classList.toggle('is-on', on);
          if (on) word.style.setProperty('--f', f.toFixed(3));
        }
      });
    };
    const state = () => {
      const v = visible();
      const playingNow = Boolean(v) && !v.paused && !v.ended;
      sec.classList.toggle('is-playing', playingNow);
      if (btn) btn.setAttribute('aria-label', playingNow ? 'Pause video' : 'Play video');
    };
    const load = (v) => { setPoster(v); if (v.preload === 'none') v.preload = 'auto'; };
    const start = (v) => { load(v); v._xpUserPaused = false; playSafe(v); };
    const seek = (v, t, andPlay) => {
      load(v);
      const goTo = () => { v.currentTime = t; mark(v); if (andPlay) playSafe(v); };
      if (v.readyState >= 1) goTo(); else { v.addEventListener('loadedmetadata', goTo, { once: true }); v.load(); }
    };
    const lay = (t) => { if (screen) screen.dataset.lay = t < 5.208 ? 'a' : t < 17.208 ? 'b' : 'c'; };
    films.forEach((v) => {
      if (key(v) === 'd') {
        ['timeupdate', 'seeked', 'loadedmetadata'].forEach((ev) => v.addEventListener(ev, () => lay(v.currentTime)));
        if ('requestVideoFrameCallback' in v) {
          const onFrame = (now, meta) => { lay(meta.mediaTime); v.requestVideoFrameCallback(onFrame); };
          v.requestVideoFrameCallback(onFrame);
        }
      }
      ['timeupdate', 'seeked'].forEach((ev) => v.addEventListener(ev, () => mark(v)));
      ['play', 'pause', 'ended'].forEach((ev) => v.addEventListener(ev, state));
      v.addEventListener('playing', () => sec.classList.add('is-live'));
      v.addEventListener('click', () => { if (v.paused) start(v); else { v._xpUserPaused = true; v.pause(); } });
    });
    if (btn) btn.addEventListener('click', () => {
      const v = visible();
      if (!v) return;
      if (v.paused) { if (!v._xpStarted && key(v) === 'd') { v._xpStarted = true; v._xpUserPaused = false; seek(v, 5.22, true); } else start(v); }
      else { v._xpUserPaused = true; v.pause(); }
    });
    chs.forEach((c) => c.addEventListener('click', () => {
      const v = visible();
      if (!v) return;
      const s = span(c, key(v));
      if (!s) return;
      v._xpStarted = true;
      v._xpUserPaused = false;
      seek(v, s[0] + 0.01, true);
    }));
    if (!hasIO) return;
    let inView = false;
    const auto = () => {
      const v = visible();
      if (!v || key(v) !== 'd') return;
      if (inView && !d.hidden && !reduced.matches && !lite() && !v._xpUserPaused) {
        if (!v._xpStarted) { v._xpStarted = true; seek(v, 5.22, true); } else if (v.paused) playSafe(v);
      } else if (!v.paused && (!inView || d.hidden)) v.pause();
    };
    const io = new IntersectionObserver((es) => es.forEach((e) => { inView = e.intersectionRatio >= 0.5; auto(); }), { threshold: [0, 0.5] });
    afterLoad(() => io.observe(screen));
    d.addEventListener('visibilitychange', auto);
    reduced.addEventListener('change', () => { if (reduced.matches) films.forEach((v) => v.pause()); else auto(); });
  }

  /* "Watch the demo": the film in a modal dialog; its sources exist only while it is open. */
  function initFilmDialog() {
    const dlg = $('[data-xp-film-dialog]');
    const open = $('[data-xp-film-open]');
    if (!dlg || !open) return;
    if (typeof dlg.showModal !== 'function') { open.hidden = true; return; }
    open.hidden = false;
    const vids = $$('video', dlg);
    // Only the cut this screen shows (desktop or phone) gets its sources: the other one is display: none.
    const arm = () => vids.filter((v) => v.getClientRects().length > 0).forEach((v) => {
      let set = false;
      $$('source[data-src]', v).forEach((s) => { if (!s.getAttribute('src')) { s.src = s.dataset.src; set = true; } });
      if (set) v.load();
    });
    const disarm = () => vids.forEach((v) => {
      v.pause();
      $$('source', v).forEach((s) => s.removeAttribute('src'));
      v.removeAttribute('src');
      v.load();
      v._xpStarted = false;
      v._xpUserPaused = false;
    });
    open.addEventListener('click', () => { dlg.showModal(); arm(); });
    const close = $('[data-xp-film-close]', dlg);
    if (close) close.addEventListener('click', () => dlg.close());
    dlg.addEventListener('click', (e) => {
      if (e.target !== dlg) return;
      const r = dlg.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dlg.close();
    });
    dlg.addEventListener('close', () => { disarm(); open.focus(); });
  }

  /* ---------- S4 "Day to day" ---------- */
  function initUses() {
    const sec = $('[data-xp-write-uses]');
    if (!sec) return;
    const stage = $('[data-xp-uses-stage]', sec);
    const rail = $('[data-xp-uses-rail]', sec);
    const tabs = $$('[data-xp-uses-tab]', sec);
    const panels = $$('[data-xp-uses-panel]', sec);
    if (!stage || !rail || !panels.length || tabs.length !== panels.length) return;
    const feats = panels.map((p) => $('.xp-feat', p));
    sec.classList.add('is-js');
    let cur = 0, seen = false, auto = true, hold = false, paused = false, inView = false, loaded = d.readyState === 'complete', steps = 0, timer = 0;
    const STEP = 6500, ROUNDS = 2;
    // Rebuild the active visual: drop .is-on (start state, instantly), then set it on the next frame (the build runs).
    const build = (i) => {
      feats.forEach((f, j) => { if (f && j !== i) f.classList.remove('is-on'); });
      const f = feats[i];
      if (!f) return;
      f.classList.remove('is-on');
      void f.offsetWidth;
      requestAnimationFrame(() => requestAnimationFrame(() => f.classList.add('is-on')));
    };
    const select = tablist(rail, tabs, panels, (i) => { stop(); go(i); });
    const go = (i) => { cur = i; select(i); if (seen) build(i); };
    const can = () => auto && !hold && !paused && inView && loaded && !d.hidden && !still();
    const clear = () => { clearTimeout(timer); timer = 0; };
    const next = () => {
      timer = 0;
      if (!can()) return;
      steps += 1;
      if (steps >= panels.length * ROUNDS) { auto = false; go(0); return; }
      go((cur + 1) % panels.length);
      timer = setTimeout(next, STEP);
    };
    const run = () => { if (can()) { if (!timer) timer = setTimeout(next, STEP); } else clear(); };
    function stop() { auto = false; clear(); }
    select(0);
    stage.addEventListener('mouseenter', () => { hold = true; run(); });
    stage.addEventListener('mouseleave', () => { hold = false; run(); });
    stage.addEventListener('focusin', () => { hold = true; run(); });
    stage.addEventListener('focusout', (e) => { if (!stage.contains(e.relatedTarget)) { hold = false; run(); } });
    panels.forEach((p) => onSwipe($('.xp-write-uses__frame', p), (dir) => { stop(); go((cur + dir + panels.length) % panels.length); }));
    pauseToggle(stage, 'app illustrations', (p) => { paused = p; run(); });
    if (!hasIO) { seen = true; build(cur); return; }
    new IntersectionObserver((es) => es.forEach((e) => {
      inView = e.intersectionRatio >= 0.5;
      if (e.intersectionRatio >= 0.35 && !seen) { seen = true; build(cur); }
      run();
    }), { threshold: [0, 0.35, 0.5] }).observe(stage);
    d.addEventListener('visibilitychange', run);
    reduced.addEventListener('change', run);
    afterLoad(() => { loaded = true; run(); });
  }

  /* ---------- S5 costs, phones: the three-dot pager under the swipe row (polish round 1) ----------
     The dots are decorative (aria-hidden; the row is a named, focusable scroller): the card nearest the row's start
     edge, in visual order (the paper card comes first on phones), is the current one. */
  function initCosts() {
    const row = $('.xp-write-costs__cards');
    const pager = $('[data-xp-costs-pager]');
    if (!row || !pager) return;
    const dots = $$('i', pager);
    const cards = Array.from(row.children);
    if (dots.length !== cards.length) return;
    pager.hidden = false;
    let raf = 0;
    const update = () => {
      raf = 0;
      if (!phone.matches) return;
      const edge = row.getBoundingClientRect().left + (parseFloat(getComputedStyle(row).paddingLeft) || 0);
      const order = cards.map((c) => c.getBoundingClientRect().left).map((x, i) => ({ i, x })).sort((a, b) => a.x - b.x);
      let k = 0;
      order.forEach((o, n) => { if (Math.abs(o.x - edge) < Math.abs(order[k].x - edge)) k = n; });
      dots.forEach((dot, n) => dot.classList.toggle('is-on', n === k));
    };
    row.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(update); }, { passive: true });
    addEventListener('resize', () => { if (!raf) raf = requestAnimationFrame(update); });
    update();
  }

  /* ---------- S7 reviews hero: the photo row and its viewer ---------- */
  function initReviewHero() {
    const fig = $('[data-xp-rhero]');
    if (!fig) return;
    const btns = $$('[data-xp-rhero-open]', fig);
    const fail = () => fig.classList.add('is-noimg');
    $$('.xp-write-rhero__ph img', fig).forEach((im) => {
      im.addEventListener('error', fail);
      if (im.complete && im.getAttribute('src') && im.naturalWidth === 0) fail();
    });
    const dlg = $('[data-xp-rhero-dialog]');
    if (!dlg || typeof dlg.showModal !== 'function' || !btns.length) return;
    const img = $('[data-xp-lb-img]', dlg);
    const alt = $('[data-xp-lb-alt]', dlg);
    const count = $('[data-xp-lb-count]', dlg);
    let i = 0, opener = null;
    const show = (n) => {
      i = (n + btns.length) % btns.length;
      const b = btns[i];
      img.src = b.dataset.full;
      img.alt = b.dataset.alt || '';
      if (alt) alt.textContent = b.dataset.alt || '';
      if (count) count.textContent = `${i + 1} / ${btns.length}`;
    };
    btns.forEach((b, n) => b.addEventListener('click', () => { opener = b; show(n); dlg.showModal(); }));
    $$('[data-xp-lb-step]', dlg).forEach((s) => s.addEventListener('click', () => show(i + Number(s.dataset.xpLbStep))));
    const close = $('[data-xp-lb-close]', dlg);
    if (close) close.addEventListener('click', () => dlg.close());
    dlg.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); show(i + 1); } else if (e.key === 'ArrowLeft') { e.preventDefault(); show(i - 1); }
    });
    dlg.addEventListener('click', (e) => {
      if (e.target !== dlg) return;
      const r = dlg.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dlg.close();
    });
    dlg.addEventListener('close', () => { img.removeAttribute('src'); if (opener) opener.focus(); });
  }

  const start = () => {
    initDemo();
    initFilm();
    initFilmDialog();
    initUses();
    initCosts();
    initReviewHero();
  };
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', start); else start();
})();
